import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';

import {
  PATH_INTERNAL_THIRD_PARTIES_DEFAULT,
  PATH_EXTERNAL_THIRD_PARTIES_DEFAULT,
  PATH_INTERNAL_PEOPLE,
  PATH_EXTERNAL_PEOPLE,
  PATH_PERSON_ADDRESSES,
  PATH_PERSON_ADDRESSES_EDIT,
  PATH_PERSON_ADDRESSES_EDIT_NEW,
  PATH_PERSON_ADDRESSES_EDIT_MODIFY,
  PATH_PERSON_ADDRESSES_EDIT_DELETE,
  PATH_PERSON_CONTACT,
  PATH_PERSON_CONTACT_EDIT,
  PATH_PERSON_GENERAL,
  PATH_PERSON_GENERAL_EDIT,
  PATH_PERSON_BILLING,
  PATH_PERSON_BILLING_EDIT,
  PATH_PERSON_BANK,
  PATH_PERSON_BANK_EDIT,
  PATH_PERSON_MEMBERSHIPS,
  PATH_PERSON_MEMBERSHIPS_EDIT,
  PATH_PERSON_PARTICIPATIONS,
  PATH_PERSON_PENNYLANE,
  PATH_PERSON_VERSIONS,
  PATH_PERSON_ADMIN,
  PATH_PERSON_ADMIN_EDIT,
  PATH_PERSON_DEFAULT,
} from '../../app/constants/paths.constant';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { Loading } from '../../components/Loading';
import { useIdParam } from '../../utils/use-id-param.util';
import { Page } from '../../contexts/NavigationContext/enums/page.enum';
import { useNavigationContext } from '../../contexts/NavigationContext/hooks/useNavigationContext.hook';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';
import { buildDocumentTitle } from '../../utils/document-title.util';
import { PageTitle } from '../../components/PageTitle';

import { PersonTabs } from './Tabs';
import { useFindOnePersonQuery } from './hooks/useFindOnePersonQuery.hook';

export { PersonTabs };

export const PersonPage = () => {
  const { setPage } = useNavigationContext();
  const id = useIdParam();
  const { addressId } = useParams();

  const { paths } = useBuildPaths(
    {
      default: PATH_PERSON_DEFAULT,
      generalInfo: PATH_PERSON_GENERAL,
      generalInfoEdit: PATH_PERSON_GENERAL_EDIT,
      contactInfo: PATH_PERSON_CONTACT,
      contactInfoEdit: PATH_PERSON_CONTACT_EDIT,
      addresses: PATH_PERSON_ADDRESSES,
      addressesEdit: PATH_PERSON_ADDRESSES_EDIT,
      addressesEditNew: PATH_PERSON_ADDRESSES_EDIT_NEW,
      addressesEditModify: PATH_PERSON_ADDRESSES_EDIT_MODIFY,
      addressesEditDelete: PATH_PERSON_ADDRESSES_EDIT_DELETE,
      billing: PATH_PERSON_BILLING,
      billingEdit: PATH_PERSON_BILLING_EDIT,
      bankInfo: PATH_PERSON_BANK,
      bankInfoEdit: PATH_PERSON_BANK_EDIT,
      memberships: PATH_PERSON_MEMBERSHIPS,
      membershipsEdit: PATH_PERSON_MEMBERSHIPS_EDIT,
      participations: PATH_PERSON_PARTICIPATIONS,
      pennylane: PATH_PERSON_PENNYLANE,
      versions: PATH_PERSON_VERSIONS,
      admin: PATH_PERSON_ADMIN,
      adminEdit: PATH_PERSON_ADMIN_EDIT,
    },
    {
      id,
      addressId,
    },
  );

  const { data, isSuccess } = useFindOnePersonQuery(id);

  const person = data?.findOnePerson;

  useEffect(() => {
    setPage(
      person?.isInternal
        ? Page.InternalThirdParties
        : Page.ExternalThirdParties,
    );
  }, [person?.isInternal, setPage]);

  const breadcrumbs = person?.isInternal
    ? [
        {
          to: PATH_INTERNAL_THIRD_PARTIES_DEFAULT,
          label: 'organisation socna',
          key: 'internalThirdParties',
        },
        {
          to: PATH_INTERNAL_PEOPLE,
          label: 'personnes',
          key: 'internalPeople',
        },
        {
          to: paths.default,
          label: 'fiche',
          key: 'internalPerson',
        },
      ]
    : [
        {
          to: PATH_EXTERNAL_THIRD_PARTIES_DEFAULT,
          label: 'tiers',
          key: 'externalThirdParties',
        },
        {
          to: PATH_EXTERNAL_PEOPLE,
          label: 'personnes',
          key: 'externalPeople',
        },
        {
          to: paths.default,
          label: 'fiche',
          key: 'externalPerson',
        },
      ];

  const beforeTitleContent = (
    <>
      <Helmet>
        <title>
          {buildDocumentTitle('Fiche Personne', data?.findOnePerson.code)}
        </title>
      </Helmet>
      <Breadcrumbs paths={breadcrumbs} />
    </>
  );

  const titleContent = <PageTitle title="Fiche Personne" />;

  if (isSuccess) {
    const person = data.findOnePerson;

    return (
      <>
        {beforeTitleContent}
        <PageTitle code={person.code} title={person.fullName} />
        <PersonTabs paths={paths} person={person} />
      </>
    );
  }

  return (
    <>
      <Loading />
      {beforeTitleContent}
      {titleContent}
    </>
  );
};
